/**
 * WatchlistPage — a live "hub" for a watchlist: per symbol, the price, 24h %change,
 * a sparkline, the OverView score/zone (chart TF) + mapped HTF zone, and quick
 * actions (chart, price alert, overview drawer). Sortable, auto-refreshing.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { CH_TYPE, chNumeric } from '../styles/appUi';
import EmptyState from '../components/common/EmptyState';
import {
  Box, Card, CardContent, Typography, Select, MenuItem, FormControl, InputLabel,
  ToggleButton, ToggleButtonGroup, IconButton, Tooltip, Table, TableHead, TableBody,
  TableRow, TableCell, TableSortLabel, Chip, CircularProgress, Collapse, Button, TextField,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import AddAlertIcon from '@mui/icons-material/AddAlert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import SearchIcon from '@mui/icons-material/Search';
import { LineChart, Line, YAxis } from 'recharts';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAppTheme } from '../hooks/useAppTheme';
import { usePersistedState } from '../hooks/usePersistedState';
import { watchlistService, WatchlistBoardRow, UnlistedTrackedRow } from '../services/watchlist.service';
import SymbolAutocomplete from '../components/common/SymbolAutocomplete';
import SymbolOverviewDrawer from '../components/common/SymbolOverviewDrawer';
import AiVerdictChip from '../components/ai/AiVerdictChip';
import PriceAlertDialog from '../components/alerts/PriceAlertDialog';

const INTERVALS = [{ v: 60, l: '1h' }, { v: 240, l: '4h' }, { v: 1440, l: '1D' }];
const ZONE_COLOR: Record<string, string> = {
  strongBuy: '#1b8f6a', probableBuy: '#26a69a', neutral: '#90a4ae', probableShort: '#ef9a9a', strongShort: '#ef5350',
};
const zoneLabel = (z?: string | null) => z ? z.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase()).trim() : '—';

// Larger, more prominent per-row sparkline (item 5).
const Sparkline: React.FC<{ data: number[] }> = ({ data }) => {
  if (!data || data.length < 2) return <Box sx={{ width: 140, height: 40 }} />;
  const up = data[data.length - 1] >= data[0];
  const rows = data.map((v, i) => ({ i, v }));
  const min = Math.min(...data), max = Math.max(...data);
  return (
    <LineChart width={140} height={40} data={rows} margin={{ top: 4, bottom: 4, left: 0, right: 0 }}>
      <YAxis hide domain={[min, max]} />
      <Line type="monotone" dataKey="v" stroke={up ? '#26a69a' : '#ef5350'} strokeWidth={1.6} dot={false} isAnimationActive={false} />
    </LineChart>
  );
};

const WatchlistPage: React.FC = () => {
  const { C } = useAppTheme();
  const navigate = useNavigate();
  const [lists, setLists] = useState<any[]>([]);
  const [wlId, setWlId] = usePersistedState<number | ''>('watchlist.hub.id', '');
  const [interval, setIntervalMin] = usePersistedState<number>('watchlist.hub.interval', 60);
  const [rows, setRows] = useState<WatchlistBoardRow[]>([]);
  const [htfInterval, setHtfInterval] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [sortKey, setSortKey] = useState<'score' | 'pctChange' | 'symbol'>('score');
  const [asc, setAsc] = useState(false);
  const [drawer, setDrawer] = useState<{ symbol: string; exchange?: string } | null>(null);
  const [alert, setAlert] = useState<{ symbol: string; exchange: string; price?: number } | null>(null);
  // Item 5 — tracked instruments not yet in ANY of the user's watchlists.
  const [unlisted, setUnlisted] = useState<UnlistedTrackedRow[]>([]);
  const [unlistedOpen, setUnlistedOpen] = usePersistedState<boolean>('watchlist.hub.unlistedOpen', false);
  const [addingKey, setAddingKey] = useState<string | null>(null);
  // Filters so the user can add a specific CATEGORY of symbols (by exchange / name).
  const [unlistedEx, setUnlistedEx] = usePersistedState<string>('watchlist.hub.unlistedEx', 'ALL');
  const [unlistedQuery, setUnlistedQuery] = useState('');

  const loadUnlisted = useCallback(async () => {
    try { const r: any = await watchlistService.unlistedTracked(); setUnlisted(r.data?.data || []); }
    catch { setUnlisted([]); }
  }, []);
  useEffect(() => { loadUnlisted(); }, [loadUnlisted]);

  // Exchanges present in the unlisted set (for the filter toggles), + the filtered view.
  const unlistedExchanges = useMemo(
    () => Array.from(new Set(unlisted.map(u => u.exchange))).sort(),
    [unlisted],
  );
  const unlistedFiltered = useMemo(() => {
    const q = unlistedQuery.trim().toUpperCase();
    return unlisted.filter(u =>
      (unlistedEx === 'ALL' || u.exchange === unlistedEx) &&
      (!q || u.symbol.toUpperCase().includes(q) || (u.name || '').toUpperCase().includes(q)));
  }, [unlisted, unlistedEx, unlistedQuery]);

  const loadLists = useCallback(async () => {
    try {
      const r: any = await watchlistService.list();
      const items = r.data?.data || [];
      setLists(items);
      setWlId(prev => (prev && items.some((l: any) => l.id === prev) ? prev : (items[0]?.id ?? '')));
    } catch { /* keep current */ }
  }, [setWlId]);
  useEffect(() => { loadLists(); }, [loadLists]);

  // This page is the ONLY place watchlists are created/deleted (the managers on
  // Strategies / Options-AT / Backtest were removed — they only SELECT one now).
  const [newWlName, setNewWlName] = useState('');
  const createWl = async () => {
    const name = newWlName.trim();
    if (!name) return;
    try {
      await watchlistService.create(name);
      setNewWlName('');
      toast.success(`Watchlist "${name}" created`);
      await loadLists();
    } catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Failed to create watchlist'); }
  };
  const deleteWl = async () => {
    if (!wlId) return;
    const nm = lists.find(l => l.id === wlId)?.name || '';
    if (!window.confirm(`Delete watchlist "${nm}"? Every page that scans or backtests it will lose it.`)) return;
    try {
      await watchlistService.remove(wlId as number);
      toast.success('Watchlist deleted');
      setWlId('');
      await loadLists();
      loadUnlisted();
    } catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Failed to delete watchlist'); }
  };

  const load = useCallback(async () => {
    if (!wlId) { setRows([]); return; }
    setLoading(true);
    try {
      const r: any = await watchlistService.board(wlId as number, interval);
      setRows(r.data?.data?.rows || []);
      setHtfInterval(r.data?.data?.htfInterval ?? null);
      setUpdatedAt(new Date());
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Failed to load watchlist');
      setRows([]);
    } finally { setLoading(false); }
  }, [wlId, interval]);

  useEffect(() => { load(); }, [load]);
  // Auto-refresh every 60s (the board recomputes scores server-side).
  useEffect(() => { const t = setInterval(load, 60_000); return () => clearInterval(t); }, [load]);

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      let av: any, bv: any;
      if (sortKey === 'symbol') { av = a.symbol; bv = b.symbol; return asc ? av.localeCompare(bv) : bv.localeCompare(av); }
      av = (a as any)[sortKey] ?? -Infinity; bv = (b as any)[sortKey] ?? -Infinity;
      return asc ? av - bv : bv - av;
    });
    return arr;
  }, [rows, sortKey, asc]);

  const toggleSort = (k: typeof sortKey) => { if (sortKey === k) setAsc(!asc); else { setSortKey(k); setAsc(false); } };

  const selectedWl = lists.find(l => l.id === wlId);
  const addSymbol = async (opt: any) => {
    if (!opt?.symbol || !wlId) return;
    try { await watchlistService.addSymbol(wlId as number, opt.symbol, opt.exchange); toast.success(`Added ${opt.symbol}`); load(); loadUnlisted(); }
    catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Failed to add'); }
  };
  const removeSymbol = async (sym: string, ex: string) => {
    if (!wlId) return;
    try { await watchlistService.removeSymbol(wlId as number, sym, ex); setRows(rows.filter(r => !(r.symbol === sym && r.exchange === ex))); loadUnlisted(); }
    catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Failed to remove'); }
  };
  // Item 5 — add a tracked-but-unlisted symbol into the current watchlist.
  const addToCurrent = async (row: UnlistedTrackedRow) => {
    if (!wlId) { toast.error('Pick a watchlist first'); return; }
    const key = `${row.symbol}|${row.exchange}`;
    setAddingKey(key);
    try {
      await watchlistService.addSymbol(wlId as number, row.symbol, row.exchange);
      setUnlisted(prev => prev.filter(u => `${u.symbol}|${u.exchange}` !== key));
      toast.success(`Added ${row.symbol} to ${selectedWl?.name || 'watchlist'}`);
      load();
    } catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Failed to add'); }
    finally { setAddingKey(null); }
  };
  const addAllUnlisted = async () => {
    // Adds the CURRENTLY-FILTERED set, so the user can bulk-add just one category
    // (e.g. all DELTA symbols, or everything matching a name search).
    if (!wlId || !unlistedFiltered.length) return;
    const batch = [...unlistedFiltered];
    setAddingKey('__all__');
    let ok = 0;
    for (const row of batch) {
      try { await watchlistService.addSymbol(wlId as number, row.symbol, row.exchange); ok++; } catch { /* skip dups/errors */ }
    }
    setAddingKey(null);
    toast.success(`Added ${ok} symbol${ok === 1 ? '' : 's'} to ${selectedWl?.name || 'watchlist'}`);
    load(); loadUnlisted();
  };

  const scoreColor = (zone?: string | null) => ZONE_COLOR[zone || 'neutral'] || '#90a4ae';

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header in three groups: WHICH list · CREATE a list · VIEW options.
          It used to be seven controls in one undifferentiated row, with the
          destructive delete sitting between the list selector and the create
          field — the two things you touch most. It is still guarded by a
          window.confirm; this is about where the eye lands and what sits next
          to what. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 800, mr: 0.5 }}>Watchlist</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Watchlist</InputLabel>
            <Select label="Watchlist" value={wlId} onChange={e => setWlId(e.target.value as number)}>
              {lists.length === 0 && <MenuItem value="" disabled>No watchlists</MenuItem>}
              {lists.map(l => <MenuItem key={l.id} value={l.id}>{l.name} ({(l.symbols || []).length})</MenuItem>)}
            </Select>
          </FormControl>
          {!!wlId && (
            <Tooltip title="Delete this watchlist">
              {/* Quiet by default; it earns its red on hover. A destructive
                  control should be reachable, not eye-catching. */}
              <IconButton size="small" onClick={deleteWl}
                sx={{ color: 'text.disabled', '&:hover': { color: C.bear, bgcolor: C.bearMuted } }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <TextField size="small" placeholder="New watchlist name" value={newWlName}
            onChange={e => setNewWlName(e.target.value)} onKeyDown={e => e.key === 'Enter' && createWl()}
            sx={{ width: 180 }} />
          <Button size="small" variant="outlined" startIcon={<AddIcon />} disabled={!newWlName.trim()} onClick={createWl}>New</Button>
        </Box>

        <ToggleButtonGroup size="small" exclusive value={interval} onChange={(_, v) => v && setIntervalMin(v)}>
          {INTERVALS.map(i => <ToggleButton key={i.v} value={i.v} sx={{ textTransform: 'none', px: 1.5 }}>{i.l}</ToggleButton>)}
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        {updatedAt && <Typography variant="caption" color="text.secondary">Updated {updatedAt.toLocaleTimeString('en-IN')}{htfInterval ? ` · HTF ${htfInterval >= 1440 ? '1D' : htfInterval + 'm'}` : ''}</Typography>}
        <Tooltip title="Refresh"><span><IconButton onClick={load} disabled={loading}>{loading ? <CircularProgress size={20} /> : <RefreshIcon />}</IconButton></span></Tooltip>
      </Box>

      {/* Add symbol */}
      {wlId && (
        <Box sx={{ maxWidth: 360, mb: 2 }}>
          <SymbolAutocomplete label="Add symbol to this watchlist" value="" onChange={addSymbol} />
        </Box>
      )}

      {/* Item 5 — tracked symbols not yet in any watchlist. New listings from the
          exchange show up here so the user can pull them in with one click. */}
      {/* Quieter than the table it sits above — it was shouting louder than the
          content, which is backwards for a secondary "here is what else exists"
          affordance. */}
      {unlisted.length > 0 && (
        <Card variant="outlined" sx={{ mb: 2, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, cursor: 'pointer' }}
            onClick={() => setUnlistedOpen(o => !o)}>
            <PlaylistAddIcon fontSize="small" sx={{ color: 'warning.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Tracked, not in any watchlist ({unlisted.length})
            </Typography>
            <Tooltip arrow title="Instruments you track that aren't yet a member of any watchlist — e.g. symbols the exchange newly listed. Add the ones you want to scan.">
              <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                new / unassigned tracked symbols
              </Typography>
            </Tooltip>
            <Box sx={{ flex: 1 }} />
            {unlistedOpen && wlId && (
              <Button size="small" variant="outlined" color="warning" startIcon={<PlaylistAddIcon />}
                disabled={addingKey === '__all__' || !unlistedFiltered.length} onClick={(e) => { e.stopPropagation(); addAllUnlisted(); }}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                {addingKey === '__all__' ? <CircularProgress size={16} /> : `Add ${unlistedFiltered.length === unlisted.length ? 'all' : `these ${unlistedFiltered.length}`} to "${selectedWl?.name || '…'}"`}
              </Button>
            )}
            <ExpandMoreIcon sx={{ transform: unlistedOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
          </Box>
          <Collapse in={unlistedOpen}>
            {/* Filters — add a specific category (by exchange) or search by name/symbol. */}
            <Box sx={{ px: 2, pt: 0.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
              <ToggleButtonGroup size="small" exclusive value={unlistedEx} onChange={(_, v) => v && setUnlistedEx(v)}>
                <ToggleButton value="ALL" sx={{ textTransform: 'none', px: 1.2, py: 0.2 }}>All</ToggleButton>
                {unlistedExchanges.map(ex => (
                  <ToggleButton key={ex} value={ex} sx={{ textTransform: 'none', px: 1.2, py: 0.2 }}>
                    {ex} ({unlisted.filter(u => u.exchange === ex).length})
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <TextField size="small" placeholder="Filter by name / symbol…" value={unlistedQuery}
                onChange={e => setUnlistedQuery(e.target.value)}
                InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.disabled' }} /> }}
                sx={{ width: 240 }} />
              {(unlistedEx !== 'ALL' || unlistedQuery) && (
                <Button size="small" variant="text" onClick={() => { setUnlistedEx('ALL'); setUnlistedQuery(''); }}>Clear</Button>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {unlistedFiltered.length} of {unlisted.length}
              </Typography>
            </Box>
            <Box sx={{ px: 2, pb: 1.5, pt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
              {unlistedFiltered.length === 0 && (
                <Typography variant="caption" color="text.secondary">No tracked symbols match this filter.</Typography>
              )}
              {unlistedFiltered.map(u => {
                const key = `${u.symbol}|${u.exchange}`;
                return (
                  <Chip key={key} size="small" variant="outlined"
                    label={`${u.symbol} · ${u.exchange}`}
                    onClick={() => setDrawer({ symbol: u.symbol, exchange: u.exchange })}
                    onDelete={wlId ? () => addToCurrent(u) : undefined}
                    deleteIcon={addingKey === key ? <CircularProgress size={14} /> : <AddIcon />}
                    sx={{ fontWeight: 600, '& .MuiChip-deleteIcon': { color: 'success.main' } }} />
                );
              })}
            </Box>
            {!wlId && <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 1.5, display: 'block' }}>Pick a watchlist above to add these.</Typography>}
          </Collapse>
        </Card>
      )}

      <Card variant="outlined">
        <CardContent sx={{ p: 0 }}>
          {!wlId ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No watchlist selected. Create one with the "New" box above, or pick one from the dropdown.</Typography>
            </Box>
          ) : loading && !rows.length ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
          ) : !rows.length ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <EmptyState kind="search" size="sm"
                title={`"${selectedWl?.name}" has no symbols yet`}
                description="Use “Add symbol to this watchlist” above, or pull one in from “Tracked, not in any watchlist”." />
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ '& td, & th': { borderColor: C.chartGrid }, '& tbody td': { py: 1.4 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell><TableSortLabel active={sortKey === 'symbol'} direction={asc ? 'asc' : 'desc'} onClick={() => toggleSort('symbol')}>Symbol</TableSortLabel></TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right"><TableSortLabel active={sortKey === 'pctChange'} direction={asc ? 'asc' : 'desc'} onClick={() => toggleSort('pctChange')}>24h %</TableSortLabel></TableCell>
                    <TableCell align="center">Trend</TableCell>
                    <TableCell align="center"><TableSortLabel active={sortKey === 'score'} direction={asc ? 'asc' : 'desc'} onClick={() => toggleSort('score')}>OverView</TableSortLabel></TableCell>
                    <TableCell align="center">HTF</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sorted.map((r) => {
                    const pc = r.pctChange;
                    return (
                      <TableRow key={`${r.symbol}-${r.exchange}`} hover>
                        <TableCell>
                          <Tooltip title={`Overview · ${r.symbol}`} arrow>
                            <Typography component="span" sx={{ fontWeight: 800, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                              onClick={() => setDrawer({ symbol: r.symbol, exchange: r.exchange })}>{r.symbol}</Typography>
                          </Tooltip>
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>{r.exchange}</Typography>
                          {/* Click-to-fetch, never automatic. A watchlist can be dozens of rows,
                              and a grid that assessed itself on render would spend real money
                              simply by being opened. The chip states its own price before it is
                              pressed. */}
                          <Box component="span" sx={{ ml: 0.6 }}>
                            <AiVerdictChip
                              symbol={r.symbol} exchange={r.exchange}
                              askSource="watchlist grid"
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.price == null ? '—' : r.price.toLocaleString('en-IN')}</TableCell>
                        <TableCell align="right">
                          {pc == null ? '—' : (
                            <Chip size="small" label={`${pc >= 0 ? '+' : ''}${pc.toFixed(2)}%`}
                              sx={{ height: 20, fontWeight: 700, fontSize: CH_TYPE.small, bgcolor: pc >= 0 ? 'rgba(38,166,154,0.16)' : 'rgba(239,83,80,0.16)', color: pc >= 0 ? '#26a69a' : '#ef5350' }} />
                          )}
                        </TableCell>
                        <TableCell align="center"><Box sx={{ display: 'flex', justifyContent: 'center' }}><Sparkline data={r.spark} /></Box></TableCell>
                        <TableCell align="center">
                          {r.score == null ? <Typography variant="caption" color="text.secondary">{r.error || '—'}</Typography> : (
                            <Tooltip title={zoneLabel(r.zone)} arrow>
                              <Chip size="small" label={r.score} sx={{ height: 22, minWidth: 46, fontWeight: 800, bgcolor: scoreColor(r.zone), color: '#fff' }} />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {r.htfZone ? <Chip size="small" variant="outlined" label={zoneLabel(r.htfZone)} sx={{ height: 18, fontSize: '0.58rem', borderColor: scoreColor(r.htfZone), color: scoreColor(r.htfZone) }} /> : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Open chart"><IconButton size="small" onClick={() => navigate(`/charts/${encodeURIComponent(r.symbol)}?exchange=${encodeURIComponent(r.exchange)}`)}><CandlestickChartIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Set price alert"><IconButton size="small" onClick={() => setAlert({ symbol: r.symbol, exchange: r.exchange, price: r.price ?? undefined })}><AddAlertIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Remove from watchlist"><IconButton size="small" onClick={() => removeSymbol(r.symbol, r.exchange)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        OverView is the same −100…+100 trend score the Charts page shows (chart TF + the aligned higher timeframe).
        Click a symbol for the overview drawer. Auto-refreshes every minute.
      </Typography>

      <SymbolOverviewDrawer open={!!drawer} symbol={drawer?.symbol || ''} exchange={drawer?.exchange} onClose={() => setDrawer(null)} />
      <PriceAlertDialog open={!!alert} onClose={() => setAlert(null)} initialSymbol={alert?.symbol} initialExchange={alert?.exchange} initialPrice={alert?.price} />
    </Box>
  );
};

export default WatchlistPage;
